import type { ClientIpHeaders } from "@app/services/security/client-ip-headers";
import type { ExperimentsVariations } from "@app/types/experiments";

import type { MaybeWhitelabel } from "@partners/partner.type";
import type { AxiosRequestHeaders } from "axios";

const ACCEPT_HEADER_V2 =
  "application/vnd.busbud+json; version=2; profile=https://schema.busbud.com/v2/anything.json";
export const ACCEPT_HEADER_V3 =
  "application/vnd.busbud+json; version=3.0.0; profile=https://schema.busbud.com/v3/anything.json";

export const ACCEPT_HEADER_V4 =
  "application/vnd.busbud+json; version=4; profile=https://schema.busbud.com/v4/anything.json";

interface ServerHeadersOptions {
  request_id?: string;
  request_id_separator?: string;
  token?: string;
  user_agent_quality?: string;
  log_level?: string | null;
  backend_region?: string;
  experiments?: ExperimentsVariations;
  feature_flags?: string | null;
  geo_headers?: Record<string, string>;
  client_ip_headers?: ClientIpHeaders;
  base_headers?: AxiosRequestHeaders;
  request_url?: string;
  whitelabel_key?: MaybeWhitelabel;
}

export function createServerHeaders(
  server_header_options: ServerHeadersOptions
): Record<string, any> {
  const headers = server_header_options.base_headers || {};

  // Create a composite request id based on the original one received by pubweb
  // This will allow correlating multiple NAPI requests to the originating
  // pubweb requests while maintaining a unique identity for each napi request.
  // Format is `{ pubweb request-id }+{ napi suffix }`
  //
  // Ref https://devcenter.heroku.com/articles/http-request-id#how-it-works
  const ts = new Date().getTime();

  if (server_header_options.request_id) {
    const separator = server_header_options.request_id_separator || "+";
    headers["x-request-id"] = server_header_options.request_id + separator + ts;
  } else {
    headers["x-request-id"] = ts;
  }

  if (server_header_options.token) {
    headers["x-busbud-token"] = server_header_options.token;
  }

  if (server_header_options.user_agent_quality) {
    headers["X-UA-Quality"] = server_header_options.user_agent_quality;
  }

  if (server_header_options.log_level) {
    headers["x-log-level"] = server_header_options.log_level;
  }

  if (server_header_options.backend_region) {
    headers["x-busbud-backend"] = server_header_options.backend_region;
  }

  if (server_header_options.experiments) {
    const xps = JSON.stringify(server_header_options.experiments);
    if (typeof Buffer === "undefined") {
      headers["x-experiments"] = btoa(xps);
    } else {
      headers["x-experiments"] = Buffer.from(xps).toString("base64");
    }
  }

  if (server_header_options.feature_flags) {
    headers["x-busbud-featureflags"] = server_header_options.feature_flags;
  }

  // Forward geo headers from client to NAPI
  if (server_header_options.geo_headers) {
    for (const key of Object.keys(server_header_options.geo_headers)) {
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      headers[key] = server_header_options.geo_headers[key];
    }
  }

  if (server_header_options.request_url) {
    headers["referer"] = server_header_options.request_url;
  }

  if (!headers["Accept"]) {
    headers["Accept"] = ACCEPT_HEADER_V2;
  }

  // Send X-CLIENT-IP and X-CLIENT-IP-HMAC signature to allow the target server
  // to verify the provided client ip is legit.
  // HMAC_SECRET needs to be sync beween pubweb and bookie.
  // https://busbud.slack.com/archives/CCFAZP6P2/p1630401779047000
  if (server_header_options.client_ip_headers) {
    headers["X-Client-IP"] =
      server_header_options.client_ip_headers["X-Client-IP"];
    headers["X-Client-IP-HMAC"] =
      server_header_options.client_ip_headers["X-Client-IP-HMAC"];
  }

  if (server_header_options.whitelabel_key) {
    headers["x-whitelabel"] = server_header_options.whitelabel_key;
  }

  return headers;
}
